import { useState, useRef } from "react";
import {
  Modal,
  Form,
  Button,
  Select,
  Checkbox,
  Space,
  Typography,
  Flex,
  Card,
  message,
  notification,
} from "antd";

import InfoCircleTooltip from "@/components/ui/InfoCircleTooltip";

import PlusIcon from "@/icons/PlusIcon";
import TrashIcon from "@/icons/TrashIcon";

import { getUnitEmployees } from "@/api/employees";
import { copyTask } from "@/api/tasks";
import routes from "@/routes";
import { useNavigate } from "react-router-dom";

const { Text, Link } = Typography;

function CopyTaskModal({
  visible,
  onCancel,
  onSave,
  task,
  units = [],
  isLoading = false,
  isAdmins = false,
  unit_uuid = "",
}) {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [executorsCache, setExecutorsCache] = useState({});
  const [rowLoading, setRowLoading] = useState({});

  const [warnFlashKey, setWarnFlashKey] = useState();
  const warnActiveRef = useRef(false);
  const warnTimerRef = useRef(null);
  const WARN_MS = 5000;

  const triggerWarnFlash = () => {
    if (warnActiveRef.current) return;

    warnActiveRef.current = true;
    setWarnFlashKey(Date.now());

    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);

    warnTimerRef.current = setTimeout(() => {
      warnActiveRef.current = false;
    }, WARN_MS);
  };

  const mapMembersToOptions = (members = []) =>
    members.map(({ uuid, lastname, firstname, patronymic }) => ({
      key: uuid,
      value: uuid,
      label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`.trim(),
    }));

  const fetchExecutors = async (unitUuid) => {
    if (!unitUuid) return [];
    if (executorsCache[unitUuid]) return executorsCache[unitUuid];

    try {
      const { members = [] } = await getUnitEmployees({ unit_uuid: unitUuid });
      const options = mapMembersToOptions(members);
      setExecutorsCache((prev) => ({ ...prev, [unitUuid]: options }));
      return options;
    } catch (e) {
      setExecutorsCache((prev) => ({ ...prev, [unitUuid]: [] }));
      return [];
    }
  };

  const isForeignSelected = () => {
    const sourceUnit = task?.unit_uuid;
    if (!sourceUnit) return false;
    const pairs = form.getFieldValue("pairs") || [];
    return pairs.some((p) => p?.unit_uuid && p.unit_uuid !== sourceUnit);
  };

  const maybeWarnOnRules = () => {
    const { copy_files, link_objects } = form.getFieldsValue([
      "copy_files",
      "link_objects",
    ]);
    if ((copy_files || link_objects) && isForeignSelected()) {
      triggerWarnFlash();
    }
  };

  const [saving, setSaving] = useState(false);

  const buildPayload = (values) => {
    const targets =
      (values.pairs || [])
        .filter((p) => p && p.unit_uuid)
        .map(({ unit_uuid, executor_uuid }) => ({
          unit_uuid,
          executor_uuid: executor_uuid || null,
        })) || [];

    return {
      task_uuid: task?.uuid,
      targets,
      copy_files: !!values.copy_files,
      link_objects: !!values.link_objects,
      make_child: !!values.make_child,
    };
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);

      if (!payload.targets.length) {
        form.setFields([
          {
            name: ["pairs", 0, "unit_uuid"],
            errors: ["Нужно выбрать хотя бы одну рабочую группу"],
          },
        ]);
        return;
      }

      setSaving(true);
      const saved = await copyTask(payload);

      const createdCount =
        saved?.created?.length ??
        saved?.result?.length ??
        saved?.task_uuids?.length ??
        payload.targets.length;

      notification.open({
        message: (
          <div style={{ fontWeight: 500 }}>
            {createdCount > 1 ? "Копии созданы" : "Копия создана"}
          </div>
        ),
        description: (
          <div style={{ whiteSpace: "normal", lineHeight: "1.4em" }}>
            {createdCount > 1 ? "Копии задачи " : "Копия задачи "}
            <Link
              onClick={() =>
                navigate(
                  isAdmins
                    ? routes.administration.taskDetail(task.uuid)
                    : unit_uuid
                      ? routes.unitTaskDetail(task.unit_uuid, task.uuid)
                      : routes.taskDetail(task.uuid),
                )
              }
            >
              {task.name}
            </Link>{" "}
            {createdCount > 1
              ? "добавлены в список задач"
              : "добавлена в список задач"}
          </div>
        ),
        icon: null,
        duration: 4,
        pauseOnHover: true,
        placement: "bottomRight",
        style: {
          border: "1px solid #B7EB8F",
          background: "#f6ffed",
          borderRadius: "8px",
          width: "500px",
        },
      });

      onSave?.(saved.transaction_uuid);
      form.resetFields();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Не удалось скопировать задачу");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel?.();
  };

  const ScrollWrap = ({ children, rowsCount }) => (
    <div
      style={{
        maxHeight: rowsCount > 5 ? 500 : "auto",
        overflowY: rowsCount > 5 ? "auto" : "visible",
        paddingRight: rowsCount > 5 ? 8 : 0,
      }}
    >
      {children}
    </div>
  );

  return (
    <Modal
      title={
        <Flex align="center" gap={12}>
          <span>{`Копирование задачи ${task?.task_number}`}</span>
          <InfoCircleTooltip
            placement="top"
            size={20}
            flashKey={warnFlashKey}
            flashDuration={WARN_MS}
          >
            Связанные ресурсы не будут скопированы в другие рабочие группы
          </InfoCircleTooltip>
        </Flex>
      }
      open={visible}
      onCancel={onCancel}
      maskClosable={false}
      width={600}
      footer={
        <Space>
          <Button onClick={handleCancel}>Отмена</Button>
          <Button
            type="primary"
            loading={isLoading || saving}
            onClick={handleOk}
          >
            Сохранить
          </Button>
        </Space>
      }
    >
      {task?.name && <Text style={{ fontSize: "13px" }}>{task?.name}</Text>}

      <Form
        form={form}
        style={{ marginTop: 20 }}
        layout="vertical"
        initialValues={{
          pairs: [{ unit_uuid: undefined, executor_uuid: undefined }],
          copy_files: false,
          link_objects: false,
          make_child: false,
        }}
      >
        <Form.List name="pairs">
          {(fields, { add, remove }) => (
            <>
              <ScrollWrap rowsCount={fields.length}>
                {fields.map(({ key, name, ...restField }, idx) => {
                  const unitUuid = form.getFieldValue([
                    "pairs",
                    name,
                    "unit_uuid",
                  ]);
                  const execOptions = unitUuid
                    ? executorsCache[unitUuid] || []
                    : [];
                  const isExecDisabled = !unitUuid || !!rowLoading[idx];

                  return (
                    <Card
                      key={key}
                      style={{
                        border: "1px solid #e9e9e9",
                        backgroundColor: "#f7f7f7",
                        marginBottom: 6,
                        boxShadow: "none",
                      }}
                    >
                      <Flex gap={3} align="end">
                        <Form.Item
                          {...restField}
                          name={[name, "unit_uuid"]}
                          label="Рабочая группа"
                          rules={[
                            {
                              required: name === 0,
                              message: "Выберите рабочую группу",
                            },
                          ]}
                          style={{
                            width: "100%",
                            minWidth: "50%",
                            marginBottom: 0,
                          }}
                        >
                          <Select
                            placeholder="Выберите группу"
                            options={units.map((u) => ({
                              label: u.name,
                              value: u.uuid,
                            }))}
                            showSearch
                            onChange={async (val) => {
                              form.setFieldValue(
                                ["pairs", name, "executor_uuid"],
                                undefined,
                              );
                              setRowLoading((s) => ({ ...s, [idx]: true }));
                              try {
                                await fetchExecutors(val);
                              } finally {
                                setRowLoading((s) => ({ ...s, [idx]: false }));
                              }
                              maybeWarnOnRules();
                            }}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "executor_uuid"]}
                          label="Исполнитель"
                          style={{ width: "100%", marginBottom: 0 }}
                        >
                          <Select
                            placeholder={
                              unitUuid
                                ? "Выберите исполнителя"
                                : "Сначала выберите группу"
                            }
                            options={execOptions}
                            disabled={isExecDisabled}
                            loading={!!rowLoading[idx]}
                            showSearch
                            notFoundContent={
                              unitUuid ? "Исполнители не найдены" : null
                            }
                            allowClear
                          />
                        </Form.Item>

                        {fields.length > 1 && name !== 0 && (
                          <Button
                            onClick={() => remove(name)}
                            icon={<TrashIcon />}
                            style={{
                              padding: 0,
                              color: "#777777",
                              background: "none",
                            }}
                          />
                        )}
                      </Flex>
                    </Card>
                  );
                })}
              </ScrollWrap>

              <Form.Item style={{ marginBottom: 20 }}>
                <Button
                  type="link"
                  onClick={() => add()}
                  icon={<PlusIcon style={{ color: "#bbbbbb" }} />}
                  style={{ padding: 0, color: "#777777" }}
                >
                  Добавить
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item
          name="copy_files"
          valuePropName="checked"
          style={{ marginBottom: 4 }}
        >
          <Checkbox>Копировать вложенные файлы</Checkbox>
        </Form.Item>

        <Form.Item
          name="link_objects"
          valuePropName="checked"
          style={{ marginBottom: 4 }}
        >
          <Checkbox
            onChange={(e) => {
              if (e.target.checked && isForeignSelected()) triggerWarnFlash();
            }}
          >
            Копировать связанные ресурсы
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="make_child"
          valuePropName="checked"
          style={{ marginBottom: 4 }}
        >
          <Checkbox>
            Связать задачу (исходная становится родительской к копиям)
          </Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default CopyTaskModal;
